import styled from "@emotion/styled";
import type { StepLiquidityPool } from "@stepfinance/liquidity";
import { formatBalance } from "@stepfinance/react";
import type { NormalizedTokenAccount } from "@stepfinance/tokens";
import type { FC } from "react";

import { useLPRatio } from "../../hooks";
import { cache } from "../../utils/cache";
import { isLiquidityPoolUntrackable } from "../../utils/pools/utils";
import { formatPercent } from "../../utils/utils";
import { NumberLabel } from "../shared";
import { PoolData } from "./PoolData";
import { PoolLabels } from "./PoolLabels";

export type PoolDataDisplayProps = {
  pool: StepLiquidityPool;
  accounts: NormalizedTokenAccount[];
};

export const PoolDataDisplay: FC<PoolDataDisplayProps> = ({ pool, accounts }) => {
  const formatShareOfPoolPercent = formatPercent(6);
  const ratio = useLPRatio(pool, accounts);
  const hasUntrackableValue = isLiquidityPoolUntrackable(pool.liquidityInUSD);
  const mintA = cache.getMint(pool.mints[0]);
  const mintB = cache.getMint(pool.mints[1]);

  const displayAValue = (
    <BalanceDisplayContainer>
      <NumberLabel>{formatBalance(pool.liquidity[0] * ratio, mintA?.decimals ?? 6)}</NumberLabel>
      {pool.mintNames[0]}
    </BalanceDisplayContainer>
  );

  const displayBValue = (
    <BalanceDisplayContainer>
      <NumberLabel>{formatBalance(pool.liquidity[1] * ratio, mintB?.decimals ?? 6)}</NumberLabel>
      {pool.mintNames[1]}
    </BalanceDisplayContainer>
  );

  return (
    <PoolDataDisplayContainer>
      <PoolLabels tokenNames={pool.mintNames} initialRowLabel="Your Share" />
      <PoolData
        pool={pool}
        initialRowValue={formatShareOfPoolPercent.format(ratio)}
        liquidityAValue={displayAValue}
        liquidityBValue={displayBValue}
        curveType={pool.curveType}
        hasUntrackableValue={hasUntrackableValue}
      />
    </PoolDataDisplayContainer>
  );
};

const BalanceDisplayContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 5px;

  font-size: 10px;

  span {
    margin: 0;
  }
`;

const PoolDataDisplayContainer = styled.div`
  display: flex;
  justify-content: space-between;
`;
